import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AllocationDonut } from "../components/allocation/AllocationDonut";
import { CountryAllocationSection } from "../components/allocation/CountryAllocationSection";
import { DriftTable } from "../components/allocation/DriftTable";
import { TargetFormModal } from "../components/allocation/TargetFormModal";
import { DriftTableCompact } from "../components/mobile/DriftTableCompact";
import { useAllocation, useSetTarget, useTarget } from "../hooks/allocation";
import { useIsMobile } from "../hooks/useIsMobile";
import type { AllocationBreakdownEntry, AllocationLevel } from "../lib/allocation";

const TOP_KEYS = ["LIQUIDE", "ANLAGEN", "IMMOBILIE", "SONSTIGE"] as const;
const SUB_KEYS = [
  "AKTIE",
  "ETF",
  "KRYPTO",
  "ANLEIHE",
  "ROHSTOFFE",
  "P2P",
  "BETEILIGUNG",
  "SONSTIGE",
] as const;

const TOP_COLORS: Record<string, string> = {
  LIQUIDE: "#3b82f6",
  ANLAGEN: "#10b981",
  IMMOBILIE: "#f59e0b",
  SONSTIGE: "#a855f7",
};
const SUB_COLORS: Record<string, string> = {
  AKTIE: "#0ea5e9",
  ETF: "#22c55e",
  KRYPTO: "#eab308",
  ANLEIHE: "#94a3b8",
  ROHSTOFFE: "#d97706",
  P2P: "#a855f7",
  BETEILIGUNG: "#14b8a6",
  SONSTIGE: "#64748b",
};

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function Allocation() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [search, setSearch] = useSearchParams();
  const stichtag = search.get("stichtag") ?? "";
  const effectiveStichtag = stichtag === "" ? undefined : stichtag;

  const allocation = useAllocation(effectiveStichtag);
  const topTarget = useTarget("TOP");
  const subTarget = useTarget("SUB");
  const setTarget = useSetTarget();
  const [editingLevel, setEditingLevel] = useState<AllocationLevel | null>(null);
  // Drill-Down-Modal: zeigt Positionen pro Klick auf eine Bucket-Zeile.
  const [drilldown, setDrilldown] = useState<{
    level: AllocationLevel;
    key: string;
    label: string;
    entries: AllocationBreakdownEntry[];
  } | null>(null);

  function openTopDrilldown(key: string) {
    if (!data) return;
    const entries = data.breakdownByTopKey[key] ?? [];
    setDrilldown({
      level: "TOP",
      key,
      label: t(`allocation.top_class_label.${key}`),
      entries,
    });
  }
  function openSubDrilldown(key: string) {
    if (!data) return;
    const entries = data.breakdownBySubKey[key] ?? [];
    setDrilldown({
      level: "SUB",
      key,
      label: t(`allocation.sub_kind_label.${key}`),
      entries,
    });
  }

  const data = allocation.data;
  const isLoading = allocation.isLoading;

  function setStichtag(val: string) {
    if (val === "") search.delete("stichtag");
    else search.set("stichtag", val);
    setSearch(search);
  }

  const topIstSlices = useMemo(
    () =>
      data?.top.items
        .filter((i) => i.actualCents > 0)
        .map((i) => ({
          key: i.key,
          label: t(`allocation.top_class_label.${i.key}`),
          value: i.actualCents,
          color: TOP_COLORS[i.key] ?? "#94a3b8",
        })) ?? [],
    [data, t],
  );
  const topSollSlices = useMemo(
    () =>
      data?.top.items.some((i) => i.targetCents !== undefined)
        ? TOP_KEYS.map((k) => {
            const it = data.top.items.find((i) => i.key === k);
            return {
              key: k,
              label: t(`allocation.top_class_label.${k}`),
              value: it?.targetCents ?? 0,
              color: TOP_COLORS[k] ?? "#94a3b8",
            };
          })
        : null,
    [data, t],
  );
  const subIstSlices = useMemo(
    () =>
      data?.sub.items
        .filter((i) => i.actualCents > 0)
        .map((i) => ({
          key: i.key,
          label: t(`allocation.sub_kind_label.${i.key}`),
          value: i.actualCents,
          color: SUB_COLORS[i.key] ?? "#94a3b8",
        })) ?? [],
    [data, t],
  );
  const subSollSlices = useMemo(
    () =>
      data?.sub.items.some((i) => i.targetCents !== undefined)
        ? SUB_KEYS.map((k) => {
            const it = data.sub.items.find((i) => i.key === k);
            return {
              key: k,
              label: t(`allocation.sub_kind_label.${k}`),
              value: it?.targetCents ?? 0,
              color: SUB_COLORS[k] ?? "#94a3b8",
            };
          })
        : null,
    [data, t],
  );

  if (isLoading || !data) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }

  const topEmpty = data.top.totalCents === 0;

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_assets")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("allocation.page_title")}</h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {formatEur(data.top.totalCents)}{" "}
            {t("allocation.subtitle_total", { defaultValue: "Gesamt" })}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: "var(--muted)" }}>
            {t("allocation.stichtag")}
          </span>
          <input
            type="date"
            value={stichtag === "" ? todayIso() : stichtag}
            onChange={(e) => setStichtag(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
        </div>
      </div>

      {/* Asset-Allocation */}
      <div className="fv-card overflow-hidden">
        <div
          className="px-6 py-4 flex items-center justify-between border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <div>
            <h2 className="text-lg font-semibold">{t("allocation.donut_title_top")}</h2>
            <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
              {formatEur(data.top.totalCents)}
            </div>
          </div>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg border text-xs font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            onClick={() => setEditingLevel("TOP")}
          >
            {t("allocation.target.edit")}
          </button>
        </div>
        <div className="p-6">
          {topEmpty ? (
            <p
              className="text-sm p-4 rounded-lg"
              style={{ background: "var(--border-soft)", color: "var(--muted)" }}
            >
              {t("allocation.empty_state")}
            </p>
          ) : (
            <div className="flex flex-col md:flex-row md:flex-wrap gap-6 items-start">
              <div className="w-full md:w-auto mx-auto max-w-[280px] md:max-w-none md:mx-0">
                <AllocationDonut
                  istSlices={topIstSlices}
                  sollSlices={topSollSlices}
                  formatEur={formatEur}
                  onSliceClick={openTopDrilldown}
                />
              </div>
              <div className="w-full md:flex-1 md:min-w-[400px]">
                {isMobile ? (
                  <DriftTableCompact
                    items={data.top.items.map((i) => ({
                      ...i,
                      label: t(`allocation.top_class_label.${i.key}`),
                    }))}
                    totalCents={data.top.totalCents}
                    formatEur={formatEur}
                    onRowClick={openTopDrilldown}
                  />
                ) : (
                  <DriftTable
                    items={data.top.items.map((i) => ({
                      ...i,
                      label: t(`allocation.top_class_label.${i.key}`),
                    }))}
                    totalCents={data.top.totalCents}
                    formatEur={formatEur}
                    onRowClick={openTopDrilldown}
                  />
                )}
              </div>
            </div>
          )}
          <div
            className="mt-4 pt-4 border-t flex flex-wrap gap-6 text-xs"
            style={{ borderColor: "var(--border)", color: "var(--muted)" }}
          >
            <span>
              {t("allocation.other_debts")}:{" "}
              <b className="num" style={{ color: "var(--text)" }}>
                {formatEur(data.otherDebtsCents)}
              </b>
            </span>
            {data.realestateLoansCents > 0 && (
              <span>
                {t("allocation.realestate_loans", { defaultValue: "Immobilien-Restschuld" })}:{" "}
                <b className="num" style={{ color: "var(--text)" }}>
                  {formatEur(data.realestateLoansCents)}
                </b>
              </span>
            )}
            {data.sonstigeSnapshotDate !== null && (
              <span>
                {t("allocation.sonstige_stale_hint", { date: data.sonstigeSnapshotDate })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Portfolio-Mix */}
      <div className="fv-card overflow-hidden">
        <div
          className="px-6 py-4 flex items-center justify-between border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <div>
            <h2 className="text-lg font-semibold">{t("allocation.donut_title_sub")}</h2>
            <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
              {formatEur(data.sub.totalCents)}
            </div>
          </div>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg border text-xs font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            onClick={() => setEditingLevel("SUB")}
          >
            {t("allocation.target.edit")}
          </button>
        </div>
        <div className="p-6">
          {data.sub.totalCents === 0 ? (
            <p
              className="text-sm p-4 rounded-lg"
              style={{ background: "var(--border-soft)", color: "var(--muted)" }}
            >
              {t("allocation.empty_state_sub")}
            </p>
          ) : (
            <div className="flex flex-col md:flex-row md:flex-wrap gap-6 items-start">
              <div className="w-full md:w-auto mx-auto max-w-[280px] md:max-w-none md:mx-0">
                <AllocationDonut
                  istSlices={subIstSlices}
                  sollSlices={subSollSlices}
                  formatEur={formatEur}
                  onSliceClick={openSubDrilldown}
                />
              </div>
              <div className="w-full md:flex-1 md:min-w-[400px]">
                {isMobile ? (
                  <DriftTableCompact
                    items={data.sub.items.map((i) => ({
                      ...i,
                      label: t(`allocation.sub_kind_label.${i.key}`),
                    }))}
                    totalCents={data.sub.totalCents}
                    formatEur={formatEur}
                    onRowClick={openSubDrilldown}
                  />
                ) : (
                  <DriftTable
                    items={data.sub.items.map((i) => ({
                      ...i,
                      label: t(`allocation.sub_kind_label.${i.key}`),
                    }))}
                    totalCents={data.sub.totalCents}
                    formatEur={formatEur}
                    onRowClick={openSubDrilldown}
                  />
                )}
              </div>
            </div>
          )}
          <div
            className="mt-4 pt-4 border-t text-xs"
            style={{ borderColor: "var(--border)", color: "var(--muted)" }}
          >
            <span>
              {t("allocation.brokerage_cash")}:{" "}
              <b className="num" style={{ color: "var(--text)" }}>
                {formatEur(data.brokerageCashCents)}
              </b>
            </span>
            {stichtag !== "" && stichtag !== todayIso() && (
              <p className="mt-1">{t("allocation.investment_history_hint")}</p>
            )}
          </div>
        </div>
      </div>

      {/* Länderverteilung — 3. Section mit Daten aus Investment-Look-Through */}
      <CountryAllocationSection
        perCountry={data.countries.perCountry}
        coveredCents={data.countries.coveredCents}
        uncoveredCents={data.countries.uncoveredCents}
      />

      {editingLevel && (
        <TargetFormModal
          open
          onClose={() => setEditingLevel(null)}
          level={editingLevel}
          keys={editingLevel === "TOP" ? TOP_KEYS : SUB_KEYS}
          initial={
            editingLevel === "TOP" ? (topTarget.data?.rows ?? null) : (subTarget.data?.rows ?? null)
          }
          onSubmit={async (rows) => {
            await setTarget.mutateAsync({ level: editingLevel, rows });
            setEditingLevel(null);
          }}
          submitting={setTarget.isPending}
        />
      )}

      {drilldown && (
        <DrilldownModal
          label={drilldown.label}
          entries={drilldown.entries}
          onClose={() => setDrilldown(null)}
          formatEur={formatEur}
        />
      )}
    </section>
  );
}

function DrilldownModal({
  label,
  entries,
  onClose,
  formatEur,
}: {
  label: string;
  entries: AllocationBreakdownEntry[];
  onClose: () => void;
  formatEur: (cents: number) => string;
}) {
  const total = entries.reduce((s, e) => s + e.valueCents, 0);
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-5 py-4 border-b sticky top-0 flex justify-between items-center"
          style={{ borderColor: "var(--border)", background: "var(--card)" }}
        >
          <div>
            <div className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>
              Zusammensetzung
            </div>
            <h2 className="text-base font-semibold">{label}</h2>
            <div className="text-xs num mt-0.5" style={{ color: "var(--muted)" }}>
              {entries.length} Position{entries.length === 1 ? "" : "en"} · {formatEur(total)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-2"
            style={{ color: "var(--muted)" }}
          >
            ✕
          </button>
        </div>
        <div className="p-5">
          {entries.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: "var(--muted)" }}>
              Keine Positionen in dieser Klasse.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-xs uppercase tracking-wider text-left border-b"
                  style={{ color: "var(--muted)", borderColor: "var(--border)" }}
                >
                  <th className="py-2">Position</th>
                  <th className="py-2 text-right">Wert</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => {
                  const share = total > 0 ? (e.valueCents / total) * 100 : 0;
                  return (
                    <tr
                      key={idx}
                      className="border-b"
                      style={{ borderColor: "var(--border-soft)" }}
                    >
                      <td className="py-2">
                        <div className="font-medium">{e.name}</div>
                        {e.detail && (
                          <div className="text-xs" style={{ color: "var(--muted)" }}>
                            {e.detail}
                          </div>
                        )}
                      </td>
                      <td className="py-2 text-right num">
                        <div className="font-semibold">{formatEur(e.valueCents)}</div>
                        <div className="text-xs" style={{ color: "var(--muted)" }}>
                          {share.toFixed(1)} %
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
